<template>
  <div id="errorlogs">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="errorlogs-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <CodeIcon :color="iconColor" class="errorlogs-icon" /> SYSTEM LOGS
            </h1>
          </div>
          <!-- Display Errors -->
          <div class="errors-table mt-14">
            <v-card-title class="px-0 pb-6 d-flex align-center">
              <!-- <h2>All Activity Logs</h2> -->
              <v-select
                v-model="selectedComponent"
                :items="componentOptions"
                label="Filter by Component"
                variant="solo"
                density="compact"
                flat
                class="mr-3"
                style="max-width: 200px"
                hide-details="auto"
              ></v-select>

              <v-select
                v-model="selectedFunction"
                :items="functionOptions"
                label="Filter by Function"
                variant="solo"
                density="compact"
                flat
                style="max-width: 200px"
                hide-details="auto"
              ></v-select>
              <v-btn
                @click="clearFilters"
                class="mx-3"
                color="primary"
                variant="text"
              >
                Clear Filters
              </v-btn>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                variant="solo"
                flat
                density="compact"
                class="search-input pb-0 pr-3"
                :style="{ backgroundColor: editorBackgroundColor }"
                :color="editorBackgroundColor"
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="errorHeaders"
              fixed-header
              :items="filteredErrors"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
              :loading="isLoading"
              loading-text="Loading Errors... Please wait"
              no-data-text="No Errors"
              class="elevation-1 users-table mt-4 clickable-row"
              :search="search"
              :sort-by="['timestamp']"
            >
              <!-- ✅ CUSTOM HEADERS SLOT -->
              <template #headers>
                <tr>
                  <th
                    class="text-left"
                    @click="setSort('additionalInfo.userEmail')"
                  >
                    Email
                    <v-icon size="small">
                      {{
                        sortBy === 'additionalInfo.userEmail'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('message')">
                    Message
                    <v-icon size="small">
                      {{
                        sortBy === 'message'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('component')">
                    Component
                    <v-icon size="small">
                      {{
                        sortBy === 'component'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('function')">
                    Function
                    <v-icon size="small">
                      {{
                        sortBy === 'function'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('timestamp')">
                    Timestamp
                    <v-icon size="small">
                      {{
                        sortBy === 'timestamp'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                </tr>
              </template>

              <!-- ✅ CUSTOM ROW RENDERING -->
              <template #item="{ item }">
                <tr @click="openErrorDetails(item)" style="cursor: pointer">
                  <td>{{ item.additionalInfo?.userEmail || 'N/A' }}</td>
                  <td>{{ item.message }}</td>
                  <td>{{ item.component }}</td>
                  <td>{{ item.function }}</td>
                  <td>
                    {{
                      item.timestamp?.seconds
                        ? new Date(
                            item.timestamp.seconds * 1000
                          ).toLocaleString()
                        : 'N/A'
                    }}
                  </td>
                </tr>
              </template>

              <!-- Optional: loading bar -->
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>
            </v-data-table>
          </div>

          <!-- Error Log Details Drawer or Modal -->
          <v-dialog v-model="dialog" max-width="800px">
            <v-card>
              <v-card-title class="d-flex align-center justify-space-between">
                <span>Error Details</span>
                <v-btn variant="text" icon @click="dialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text>
                <JsonViewer
                  v-if="selectedError"
                  :value="selectedError"
                  :expanded="true"
                  :expand-depth="Infinity"
                  :copyable="true"
                  theme="dark"
                  class="rounded"
                />
              </v-card-text>
              <v-card-actions>
                <v-btn color="primary" @click="dialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-snackbar
            v-model="snackbar"
            color="#592892"
            :timeout="timeout"
            location="top"
          >
            <span v-html="snackbarMsg"></span>
            <template #actions="{ attrs }">
              <v-btn
                variant="text"
                v-bind="attrs"
                @click="snackbar = false"
                class="font-weight-bold"
              >
                Close
              </v-btn>
            </template>
          </v-snackbar>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import CodeIcon from '@/assets/icons/CodeIcon.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { toRaw } from 'vue'

  export default {
    name: 'ErrorsLogs',
    components: {
      HowToUseThisPage,
      CodeIcon,
    },
    data() {
      return {
        snackbar: false,
        snackbarMsg: '',
        search: '',
        timeout: 6000,
        customerInfo: null,
        loadingErrors: false,
        errorsLoaded: false,
        errors: [],
        errorHeaders: [
          { text: 'Email', value: 'additionalInfo.userEmail' },
          { text: 'Message', value: 'message' },
          { text: 'Component', value: 'component' },
          { text: 'Function', value: 'function' },
          { text: 'Timestamp', value: 'timestamp' },
        ],
        isLoading: false,
        creatingCoupon: false,
        dialog: false, // Controls modal visibility
        selectedError: null, // Holds the selected error log data
        selectedComponent: 'All Components',
        selectedFunction: 'All Functions',
        componentOptions: ['All Components'],
        functionOptions: ['All Functions'],
        sortBy: 'timestamp',
        sortDesc: true,
      }
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      editorBackgroundColor() {
        return currentTheme.value === 'dark' ? '#272727' : '#ffffff'
      },
      jsonViewerTheme() {
        return currentTheme.value === 'dark' ? 'dark' : 'light'
      },
      safeError() {
        return this.selectedError
          ? JSON.parse(JSON.stringify(this.selectedError))
          : null
      },
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      filteredErrors() {
        let result = this.errors.filter((error) => {
          const matchesComponent =
            this.selectedComponent === 'All Components' ||
            error.component === this.selectedComponent

          const matchesFunction =
            this.selectedFunction === 'All Functions' ||
            error.function === this.selectedFunction

          return matchesComponent && matchesFunction
        })

        if (this.sortBy) {
          result.sort((a, b) => {
            const valA = this.deepValue(a, this.sortBy)
            const valB = this.deepValue(b, this.sortBy)

            if (valA < valB) return this.sortDesc ? 1 : -1
            if (valA > valB) return this.sortDesc ? -1 : 1
            return 0
          })
        }

        return result
      },
    },
    async created() {
      this.isloading = true
      await this.fetchErrors()
      this.clearFilters()
    },
    methods: {
      deepValue(obj, path) {
        return path.split('.').reduce((o, p) => o?.[p], obj)
      },
      setSort(field) {
        if (this.sortBy === field) {
          this.sortDesc = !this.sortDesc
        } else {
          this.sortBy = field
          this.sortDesc = true
        }
      },
      async fetchErrors() {
        try {
          this.isLoading = true
          const errorLogsRef = db.collection('errorLogs')
          const snapshot = await errorLogsRef.get()

          const errorsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          // Add 'All Components' and 'All Functions' to the options
          this.componentOptions = [
            'All Components',
            ...new Set(errorsData.map((e) => e.component)),
          ]
          this.functionOptions = [
            'All Functions',
            ...new Set(errorsData.map((e) => e.function)),
          ]

          this.errors = errorsData
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.snackbar = true
          this.snackbarMsg = 'Failed to fetch error logs. Please try again.'
          console.error('Error fetching error logs:', error)
        }
      },
      openErrorDetails(error) {
        const rawError = toRaw(error)

        // Clone and convert timestamp to readable string (optional)
        const safeError = {
          ...rawError,
          timestamp: rawError.timestamp?.seconds
            ? new Date(rawError.timestamp.seconds * 1000).toISOString()
            : rawError.timestamp,
        }

        this.selectedError = safeError
        this.dialog = true
      },
      clearFilters() {
        this.selectedComponent = 'All Components'
        this.selectedFunction = 'All Functions'
        this.search = ''
        this.sortBy = 'timestamp'
        this.sortDesc = true
      },
    },
  }
</script>

<style scoped>
  .errorlogs-container {
    margin-bottom: 25%;
  }
  .errorlogs-container .errorlogs-icon {
    margin-right: 10px;
  }
  .coupon-table {
    margin-bottom: 25%;
  }
  .dark-theme {
    background-color: #333;
    color: #fff;
    padding: 20px;
    border-radius: 5px;
  }
  .dark-theme .__json-pretty__value-key,
  .dark-theme .__json-pretty__value-string,
  .dark-theme .__json-pretty__value-number,
  .dark-theme .__json-pretty__value-boolean,
  .dark-theme .__json-pretty__value-null {
    color: #fff;
  }
  .dark-theme :deep(.vjs-tree-node:hover) {
    background-color: #5a5a5a;
    /* color: #121212; */
  }
  .clickable-row :deep(tbody tr) {
    cursor: pointer;
  }
</style>
